/**
 * How much room there is, and whether the browser has promised to keep what we
 * put in it.
 *
 * Two separate questions, and the app had been asking neither:
 *
 *  - **Quota.** `bytesHeld` told us what the app was carrying, not what the
 *    device would still accept. A phone with 300 MB free and 1,500 photos left
 *    to take is going to fail, and the place to find that out is the car park.
 *  - **Persistence.** Without `navigator.storage.persist()`, IndexedDB is
 *    *best-effort*: the browser may evict the whole origin under pressure, and
 *    Safari evicts a non-installed web app's data after seven days of no use.
 *    Nothing warns the engineer; the photos are simply gone next time.
 *
 * Everything here degrades to "unknown" rather than throwing. A browser that
 * does not implement the Storage API must not stop an engineer working — but it
 * must not be reported as healthy either, so unknown is its own answer and the
 * readiness check treats it as such.
 */

export interface StorageFacts {
  /** Total the origin may use, in bytes. Null when the browser will not say. */
  quotaBytes: number | null;
  /** What the origin is using now. Null when unknown. */
  usageBytes: number | null;
  /** True only when the browser has promised not to evict without being asked. */
  persisted: boolean;
  /** False when the Storage API is absent — unknown, not healthy. */
  supported: boolean;
}

interface StorageLike {
  persist?: () => Promise<boolean>;
  persisted?: () => Promise<boolean>;
  estimate?: () => Promise<{ quota?: number; usage?: number }>;
}

const storageOf = (nav: unknown): StorageLike | null => {
  const s = (nav as { storage?: StorageLike } | undefined)?.storage;
  return s && typeof s === 'object' ? s : null;
};

/**
 * Asks the browser to keep this origin's data.
 *
 * Chrome grants it silently on an installed or well-used app; Safari ties it to
 * being added to the Home Screen; Firefox prompts. Called at start-up rather
 * than at first capture, because a refusal is something the engineer needs to
 * know about before the shift, not during it.
 */
export async function requestPersistence(nav: unknown = globalThis.navigator): Promise<boolean> {
  const s = storageOf(nav);
  if (!s?.persist) return false;
  try {
    if (s.persisted && (await s.persisted())) return true;
    return await s.persist();
  } catch {
    // A refusal and a thrown call mean the same thing to the engineer.
    return false;
  }
}

export async function storageFacts(nav: unknown = globalThis.navigator): Promise<StorageFacts> {
  const s = storageOf(nav);
  if (!s?.estimate && !s?.persisted) {
    return { quotaBytes: null, usageBytes: null, persisted: false, supported: false };
  }
  let quotaBytes: number | null = null;
  let usageBytes: number | null = null;
  let persisted = false;
  try {
    const est = s.estimate ? await s.estimate() : {};
    quotaBytes = typeof est.quota === 'number' ? est.quota : null;
    usageBytes = typeof est.usage === 'number' ? est.usage : null;
  } catch {
    /* unknown */
  }
  try {
    persisted = s.persisted ? await s.persisted() : false;
  } catch {
    persisted = false;
  }
  return { quotaBytes, usageBytes, persisted, supported: true };
}

/** What the app's compression produces — see `capture/photo.ts`. */
export const BYTES_PER_PHOTO = 400 * 1024;

/**
 * How many more photos will fit, with a margin.
 *
 * The margin is not politeness. A device that fills completely does not fail
 * one write cleanly — the service worker, the queue and the read cache all start
 * failing together, and a half-written IndexedDB is worse than a full one.
 */
export function photoHeadroom(facts: StorageFacts, marginFraction = 0.15): number | null {
  if (facts.quotaBytes === null || facts.usageBytes === null) return null;
  const free = facts.quotaBytes - facts.usageBytes;
  const usable = free - facts.quotaBytes * marginFraction;
  return Math.max(0, Math.floor(usable / BYTES_PER_PHOTO));
}
